import { Auction34Tab } from '@/components/Auction34/types';

/** handoff-mockup/assets 를 복사해 둔 위치 */
export const A34_ASSETS = '/images/auction34';

export const AUCTION34_TABS: { id: Auction34Tab; label: string }[] = [
  { id: 'guide', label: 'GUIDE' },
  { id: 'select', label: 'SELECT' },
  { id: 'brand', label: 'BRAND' },
  { id: 'retech', label: 'RE:TECH' },
];

export const tabButtonId = (tab: Auction34Tab) => `a34-tab-${tab}`;
export const tabPanelId = (tab: Auction34Tab) => `a34-panel-${tab}`;

export interface Auction34Hero {
  eyebrow: string;
  titleSrc: string;
  titleAlt: string;
  desc?: string;
}

/** GUIDE 는 히어로가 없습니다. */
export const AUCTION34_HERO: Partial<Record<Auction34Tab, Auction34Hero>> = {
  select: {
    eyebrow: '지금 가장 주목받는 핫 아이템 셀렉션',
    titleSrc: `${A34_ASSETS}/title-select-v2.png`,
    titleAlt: 'SELECT',
    desc: '가전부터 리빙, 육아, 취미 아이템까지 지금 가장 주목받는 핫 아이템을 한곳에 모았어요.',
  },
  brand: {
    eyebrow: '자사 브랜드별 셀렉션',
    titleSrc: `${A34_ASSETS}/title-brand-v2.png`,
    titleAlt: 'BRAND',
    desc: '자사 브랜드별 상품을 한곳에 모아 확인해보세요.',
  },
  retech: {
    eyebrow: '사내에서 사용된 전자기기',
    titleSrc: `${A34_ASSETS}/title-retech-v2.png`,
    titleAlt: 'RE:TECH',
  },
};

export const A34_SIGN_SRC = `${A34_ASSETS}/sign-34-mansion-v2.png`;
export const A34_SIGN_ALT = '34 MANSION 오늘만 열리는 맨션상점';

/**
 * 맨션상점 운영 시간 (ISO8601 · KST). PORTAL-13257 에서 BE 와 합의한 값입니다.
 * BE 스케줄 API 의 opensAt · bidStartAt · bidEndAt 과 1:1 로 대응합니다.
 *
 * opensAt(문 여는 시각)과 bidStartAt(입찰 시작)은 다릅니다 — 10:00 에 상점을 열어
 * 상품을 열람할 수 있지만 입찰은 12:00 부터입니다. 시각을 쓰는 문구가 둘 중 어느
 * 쪽을 가리키는지 확인하고 골라야 합니다.
 * closesAt 이 지나면 GUIDE 탭이 종료 안내로 바뀌고 입찰 폼이 잠깁니다.
 *
 * 화면 전환은 클라이언트 시각 기준이라 PC 시계를 돌리면 우회할 수 있습니다.
 * 입찰 자체의 시간 차단은 서버 몫이고, 여기서는 화면 전환까지만 책임집니다.
 */
export const A34_SCHEDULE = {
  opensAt: '2026-08-21T10:00:00+09:00',
  bidStartAt: '2026-08-21T12:00:00+09:00',
  closesAt: '2026-08-21T16:00:00+09:00',
} as const;

/**
 * 이벤트 종료(접속 차단) 시각 — PORTAL-13832.
 * 이 시각부터 루트('/') 는 맨션상점 대신 종료 안내 화면만 보여줍니다.
 *
 * A34_SCHEDULE.closesAt(입찰 마감 16:00)과는 다른 값입니다. 입찰이 마감된 뒤에도
 * 낙찰 안내를 위해 8/28 까지는 페이지를 열어두고, 그 다음 날부터 접속을 막습니다.
 * 즉 closesAt 은 "입찰이 끝난 시각", 이 값은 "페이지를 닫는 시각" 입니다.
 *
 * 판정은 pages/index.tsx 의 getServerSideProps 에서 SSR 서버 시각으로 합니다.
 * ClosedState · PreOpenState 와 달리 클라이언트 시각을 보지 않으므로 PC 시계를
 * 돌려도 우회되지 않습니다. 오프셋(+09:00)을 붙인 절대 시각이라 서버 TZ 와도
 * 무관합니다 — 로컬 시각 문자열 파싱이나 getHours() 로 비교하지 마세요.
 */
export const A34_EVENT_CLOSED_AT = '2026-08-29T00:00:00+09:00';

/** 이벤트 종료(접속 차단) 여부. 서버 시각으로 판정하도록 SSR 에서만 호출합니다. */
export const isAuction34EventClosed = (now: Date = new Date()) =>
  now.getTime() >= Date.parse(A34_EVENT_CLOSED_AT);

/** 종료 안내 팻말. 종료 전에는 렌더되지 않아 내려받지도 않습니다. */
export const A34_CLOSED_SIGN_SRC = `${A34_ASSETS}/sign-closed.png`;
export const A34_CLOSED_SIGN_ALT = 'CLOSED — 맨션상점 종료 안내 팻말';

/** 종료 후 입찰 폼·최고 입찰가 자리에 함께 쓰는 안내 문구 */
export const A34_CLOSED_BID_MESSAGE =
  '입찰이 종료되었습니다. 낙찰 결과는 인트라넷 공지로 안내됩니다.';

/** 오픈 전 안내 팻말. 오픈 후에는 렌더되지 않아 내려받지도 않습니다. */
export const A34_PREOPEN_SIGN_SRC = `${A34_ASSETS}/sign-coming-soon.png`;
export const A34_PREOPEN_SIGN_ALT = 'COMING SOON — 맨션상점 오픈 예정 팻말';

/**
 * 오픈 전 입찰 폼에 함께 쓰는 안내 문구.
 * 받는 값은 입찰 시작 시각(useAuction34Schedule 의 bidStartLabel)입니다 — 문구의
 * "오픈 시각" 은 상점 문이 열리는 10:00 이 아니라 입찰이 열리는 시각을 가리킵니다.
 * 시각은 인자로만 받습니다(하드코딩하지 않습니다).
 */
export const a34PreopenBidMessage = (bidStartLabel: string) =>
  `입찰은 오픈 시각(${bidStartLabel}) 이후 가능합니다.`;

/**
 * SELECT/BRAND/RE:TECH 목록 상단 미리보기 안내 — 입찰 시작 전에도 상품은 열람할 수
 * 있습니다. a34PreopenBidMessage 와 같은 이유로 입찰 시작 시각을 받습니다.
 */
export const a34PreviewNote = (bidStartLabel: string) =>
  `미리보기 화면입니다 · 입찰은 오픈 시각(${bidStartLabel}) 이후 가능합니다.`;

/**
 * 입찰 모달의 최고 입찰가 확인 가능 시간 안내.
 * 실제 허용 시간은 서버가 판단하며(그 외 시간의 조회는 서버가 거절합니다),
 * 화면에는 이 고정 문구만 노출합니다.
 */
export const A34_TOP_BID_TIMES = {
  title: '최고 입찰가 확인 가능 시간',
  slots: ['14:00:00 ~ 14:04:59', '15:00:00 ~ 15:04:59', '15:45:00 ~ 15:49:59'],
} as const;

/** RE:TECH 탭 상단 유의사항. 문구 최종본은 디자인 확정 후 교체될 수 있습니다. */
export const A34_RETECH_NOTICE = {
  title: '구매 전 확인해 주세요',
  items: [
    { term: '점검 상태', desc: '사전 점검 완료 (정상 작동 확인)' },
    {
      term: '기기 스펙',
      desc: '출시 3년 이상 경과 · Windows OS 사용 가능',
    },
    {
      term: '기기 상태',
      desc: '사내 사용감(스크래치)과 배터리 자연 소모가 있을 수 있습니다',
    },
    {
      term: '보증 및 관리',
      desc: '무상 A/S 보증이 종료된 상품으로, 수령 후 유지·수리는 본인 부담입니다',
    },
    {
      term: '제공 구성품',
      desc: '본체와 충전기(어댑터)만 제공되며 그 외 부속품은 없습니다',
    },
    { term: '취소·환불', desc: '낙찰 후 취소·교환·환불은 불가합니다' },
  ],
} as const;
